import React, { useState } from "react";
import ReactDOM from "react-dom";
import * as AlfredoStyles from "../../../styles/components/configuration/modals/CreateSKUModal";
import {
  EntradaDeConversao,
  RENDIMENTO_INVALIDO,
  UNIDADES_DE_COMPRA,
  UNIDADES_DE_CONSUMO,
  UNIDADES_DE_ESTOQUE,
  VALOR_OUTRA_UNIDADE_DE_COMPRA,
  montarLinhaDeConversao,
  paraNumero,
  rendimentoEhValido,
} from "./sku/unidades";
import { useModalAcessivel } from "../../../hooks/useModalAcessivel";

// A lista fechada de campos existe no PATCH (edição), NÃO na criação:
// `createOrganizationInfo` grava `organizationInfoValue` como veio, então tudo
// o que se digita aqui persiste. Havia um aviso permanente nesta tela dizendo o
// contrário — alarme falso na tela de criação é tão ruim quanto silêncio na de
// edição, porque ensina o usuário a ignorar o aviso que importa.

interface Props {
  handleCloseModal: () => void;
  handleSave: (objectType: string, payload?: any) => any;
  /**
   * Aceito e IGNORADO. O modal desestruturava `setRef` e não chamava em lugar
   * nenhum: `formRefs.current` ficava vazio e o POST saía com todos os campos
   * `undefined`. O formulário agora é estado local e vai inteiro no payload de
   * `handleSave`. A prop continua declarada só porque `configuration/index.tsx`
   * ainda a passa — some daqui quando sumir de lá.
   */
  setRef?: (name: string) => (element: any) => void;
}

const CreateSKUModal: React.FC<Props> = ({ handleCloseModal, handleSave }) => {
  // Esc fecha, o foco fica preso dentro e volta para quem abriu, e o leitor
  // de tela anuncia a janela pelo titulo. Ver hooks/useModalAcessivel.ts.
  const { refDoModal, propsDoDialogo } = useModalAcessivel({
    aoFechar: handleCloseModal,
    idDoTitulo: "titulo-createskumodal",
  });

  const [form, setForm] = useState({
    organizationInfoName: "",
    organizationInfoDescription: "",
    organizationInfoValue: {
      skuType: "",
      skuUnitOfMeasure: "",
      skuSuggestedPrice: "",
      skuBarcode: "",
      skuStatus: true,
      skuCost: "",
      skuWeight: "",
      skuDimensions: "",
      // Compra, estoque e consumo são TRÊS unidades diferentes do mesmo SKU.
      // Compra-se caixa, guarda-se kg, consome-se g — misturar as três em um
      // "unidade de medida" só é o que faz o inventário nunca fechar.
      unidadeCompra: "",
      unidadeCompraOutra: "",
      unidadeEstoque: "",
      unidadeConsumo: "",
      fatorConversaoCompraEstoque: "",
      fatorRendimento: "",
      perecivel: false,
      validadeControlada: false,
      estoqueSeguranca: "",
      // productServiceTax NÃO entra aqui de propósito: é false em 504 de 504
      // registros reais. Campo que nunca varia não é configuração, é ruído —
      // ocupa linha de formulário e sugere uma decisão que ninguém toma.
    },
  });
  const [aviso, setAviso] = useState<{
    tom: "ok" | "erro" | "atencao";
    texto: string;
  } | null>(null);
  const [salvando, setSalvando] = useState(false);

  const valores = form.organizationInfoValue;

  const handleLocalChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value, type } = e.target;
    const conteudo =
      type === "checkbox" ? (e.target as HTMLInputElement).checked : value;

    if (name.startsWith("organizationInfoValue.")) {
      const campo = name.replace("organizationInfoValue.", "");
      setForm((prev) => ({
        ...prev,
        organizationInfoValue: {
          ...prev.organizationInfoValue,
          [campo]: conteudo,
        },
      }));
    } else {
      setForm((prev) => ({ ...prev, [name]: conteudo }));
    }
  };

  // Recalculada a cada tecla: a conversão é conferência, e conferência que só
  // aparece depois de salvar chega tarde.
  const entradaDeConversao: EntradaDeConversao = {
    unidadeCompra: valores.unidadeCompra,
    unidadeCompraOutra: valores.unidadeCompraOutra,
    unidadeEstoque: valores.unidadeEstoque,
    unidadeConsumo: valores.unidadeConsumo,
    fatorConversaoCompraEstoque: valores.fatorConversaoCompraEstoque,
    fatorRendimento: valores.fatorRendimento,
  };
  const linhaDeConversao = montarLinhaDeConversao(entradaDeConversao);

  const handleSubmit = async () => {
    // A unidade de medida entra na conta dos obrigatórios porque o rótulo dela
    // tem asterisco: `required` em <select> não vale nada aqui (não existe
    // <form>, o clique é num botão comum), então a estrela prometia uma trava
    // que ninguém aplicava e o SKU nascia sem unidade na listagem.
    if (
      !form.organizationInfoName ||
      !form.organizationInfoDescription ||
      !valores.skuType ||
      !valores.skuUnitOfMeasure
    ) {
      setAviso({
        tom: "erro",
        texto:
          "Preencha os campos obrigatórios: código, nome, tipo de SKU e " +
          "unidade de medida.",
      });
      return;
    }

    const rendimento = paraNumero(valores.fatorRendimento);
    if (rendimento !== null && !rendimentoEhValido(rendimento)) {
      setAviso({ tom: "erro", texto: RENDIMENTO_INVALIDO });
      return;
    }

    const fator = paraNumero(valores.fatorConversaoCompraEstoque);
    if (fator !== null && fator <= 0) {
      setAviso({
        tom: "erro",
        texto:
          "O fator de conversão é quanto de estoque vem em UMA unidade de " +
          "compra: tem de ser maior que zero.",
      });
      return;
    }

    const estoqueSeguranca = paraNumero(valores.estoqueSeguranca);
    if (estoqueSeguranca !== null && estoqueSeguranca < 0) {
      setAviso({
        tom: "erro",
        texto: "O estoque de segurança não pode ser negativo.",
      });
      return;
    }

    // O texto livre só existe enquanto a unidade for "outra". Trocar para
    // "caixa de 12" depois de ter escrito "bandeja de 6" deixava a frase morta
    // no estado, e ela ia junto para o banco — depois alguém lê "bandeja de 6"
    // num SKU comprado em caixa e acredita.
    const paraEnviar = {
      ...form,
      organizationInfoValue: {
        ...valores,
        unidadeCompraOutra:
          valores.unidadeCompra === VALOR_OUTRA_UNIDADE_DE_COMPRA
            ? valores.unidadeCompraOutra.trim()
            : "",
      },
    };

    setSalvando(true);
    try {
      const resultado = await handleSave("createSKU", paraEnviar);
      // Quem fecha o modal é o shell. Aqui só se mostra a recusa que ele
      // devolveu — fechar por conta própria no erro é a mentira de sempre.
      if (resultado && resultado.ok === false) {
        setAviso({ tom: resultado.tom || "erro", texto: resultado.texto });
      }
    } finally {
      setSalvando(false);
    }
  };

  return ReactDOM.createPortal(
    <AlfredoStyles.ModalOverlay>
      <AlfredoStyles.ModalContent ref={refDoModal} {...propsDoDialogo} medium>
        <AlfredoStyles.ModalHeader>
          <AlfredoStyles.ModalTitle id="titulo-createskumodal">
            <AlfredoStyles.MaterialIcons>
              add_circle
            </AlfredoStyles.MaterialIcons>
            Novo SKU
          </AlfredoStyles.ModalTitle>
          <AlfredoStyles.ModalCloseButton onClick={handleCloseModal}>
            <AlfredoStyles.MaterialIcons>close</AlfredoStyles.MaterialIcons>
          </AlfredoStyles.ModalCloseButton>
        </AlfredoStyles.ModalHeader>

        <AlfredoStyles.ModalBody>
          <AlfredoStyles.FormContainer>
            <AlfredoStyles.FormSection>
              <AlfredoStyles.SectionTitle>
                Informações Básicas do SKU
              </AlfredoStyles.SectionTitle>

              <AlfredoStyles.FormGrid>
                <AlfredoStyles.TextInput>
                  <input
                    type="text"
                    name="organizationInfoName"
                    value={form.organizationInfoName}
                    onChange={handleLocalChange}
                    required
                  />
                  <label>Código do SKU *</label>
                  <span className="tooltip">
                    Código único de identificação do produto
                  </span>
                </AlfredoStyles.TextInput>

                <AlfredoStyles.TextInput>
                  <input
                    type="text"
                    name="organizationInfoDescription"
                    value={form.organizationInfoDescription}
                    onChange={handleLocalChange}
                    required
                  />
                  <label>Nome do SKU *</label>
                  <span className="tooltip">Nome descritivo do produto</span>
                </AlfredoStyles.TextInput>
              </AlfredoStyles.FormGrid>
            </AlfredoStyles.FormSection>

            <AlfredoStyles.FormSection>
              <AlfredoStyles.SectionTitle>
                Classificação e Unidades
              </AlfredoStyles.SectionTitle>

              <AlfredoStyles.FormGrid>
                <AlfredoStyles.DropdownInput>
                  <select
                    name="organizationInfoValue.skuType"
                    value={valores.skuType}
                    onChange={handleLocalChange}
                    required
                  >
                    <option value="" disabled>
                      Selecione o tipo
                    </option>
                    <option value="1">Produto Final</option>
                    <option value="2">Produto Insumo</option>
                    <option value="3">Produto Combo</option>
                    <option value="4">Produto Dividido</option>
                    <option value="5">Embalagem</option>
                  </select>
                  <label>Tipo de SKU *</label>
                  <span className="tooltip">
                    Categoria principal do produto
                  </span>
                </AlfredoStyles.DropdownInput>

                <AlfredoStyles.DropdownInput>
                  <select
                    name="organizationInfoValue.skuUnitOfMeasure"
                    value={valores.skuUnitOfMeasure}
                    onChange={handleLocalChange}
                    required
                  >
                    <option value="" disabled>
                      Selecione a unidade
                    </option>
                    <option value="1">Unidade</option>
                    <option value="2">Kg</option>
                    <option value="3">L</option>
                    <option value="4">M</option>
                  </select>
                  <label>Unidade de Medida *</label>
                  <span className="tooltip">
                    Unidade padrão para controle de estoque
                  </span>
                </AlfredoStyles.DropdownInput>
              </AlfredoStyles.FormGrid>
            </AlfredoStyles.FormSection>

            <AlfredoStyles.FormSection>
              <AlfredoStyles.SectionTitle>
                Compra, Estoque e Consumo
              </AlfredoStyles.SectionTitle>

              <AlfredoStyles.FormGrid>
                <AlfredoStyles.DropdownInput>
                  <select
                    name="organizationInfoValue.unidadeCompra"
                    value={valores.unidadeCompra}
                    onChange={handleLocalChange}
                  >
                    <option value="" disabled>
                      Selecione a unidade de compra
                    </option>
                    {UNIDADES_DE_COMPRA.map((opcao) => (
                      <option key={opcao.valor} value={opcao.valor}>
                        {opcao.rotulo}
                      </option>
                    ))}
                  </select>
                  <label>Unidade de Compra</label>
                  <span className="tooltip">
                    Como o produto chega do fornecedor
                  </span>
                </AlfredoStyles.DropdownInput>

                <AlfredoStyles.TextInput>
                  <input
                    type="text"
                    name="organizationInfoValue.unidadeCompraOutra"
                    value={valores.unidadeCompraOutra}
                    onChange={handleLocalChange}
                    disabled={
                      valores.unidadeCompra !== VALOR_OUTRA_UNIDADE_DE_COMPRA
                    }
                    placeholder="ex.: bandeja de 6"
                  />
                  <label>Outra unidade de compra</label>
                  <span className="tooltip">
                    Só vale quando a unidade de compra é "outra"
                  </span>
                </AlfredoStyles.TextInput>

                <AlfredoStyles.DropdownInput>
                  <select
                    name="organizationInfoValue.unidadeEstoque"
                    value={valores.unidadeEstoque}
                    onChange={handleLocalChange}
                  >
                    <option value="" disabled>
                      Selecione a unidade de estoque
                    </option>
                    {UNIDADES_DE_ESTOQUE.map((opcao) => (
                      <option key={opcao.valor} value={opcao.valor}>
                        {opcao.rotulo}
                      </option>
                    ))}
                  </select>
                  <label>Unidade de Estoque</label>
                  <span className="tooltip">Como o saldo é contado</span>
                </AlfredoStyles.DropdownInput>

                <AlfredoStyles.DropdownInput>
                  <select
                    name="organizationInfoValue.unidadeConsumo"
                    value={valores.unidadeConsumo}
                    onChange={handleLocalChange}
                  >
                    <option value="" disabled>
                      Selecione a unidade de consumo
                    </option>
                    {UNIDADES_DE_CONSUMO.map((opcao) => (
                      <option key={opcao.valor} value={opcao.valor}>
                        {opcao.rotulo}
                      </option>
                    ))}
                  </select>
                  <label>Unidade de Consumo</label>
                  <span className="tooltip">
                    Como a receita baixa do estoque
                  </span>
                </AlfredoStyles.DropdownInput>

                <AlfredoStyles.TextInput>
                  <input
                    type="number"
                    step="0.000001"
                    name="organizationInfoValue.fatorConversaoCompraEstoque"
                    value={valores.fatorConversaoCompraEstoque}
                    onChange={handleLocalChange}
                    placeholder="0,000000"
                  />
                  <label>Fator Compra → Estoque</label>
                  <span className="tooltip">
                    Quanto de unidade de estoque vem em UMA unidade de compra
                  </span>
                </AlfredoStyles.TextInput>

                <AlfredoStyles.TextInput>
                  <input
                    type="number"
                    step="0.000001"
                    name="organizationInfoValue.fatorRendimento"
                    value={valores.fatorRendimento}
                    onChange={handleLocalChange}
                    placeholder="0,000000"
                  />
                  <label>Fator de Rendimento</label>
                  <span className="tooltip">
                    Maior que 0 e até 1: 1 kg de alface que dá 0,7 kg limpo é
                    0,7
                  </span>
                </AlfredoStyles.TextInput>

                <AlfredoStyles.CurrencyPreview>
                  <AlfredoStyles.MaterialIcons>
                    calculate
                  </AlfredoStyles.MaterialIcons>
                  {linhaDeConversao.texto}
                </AlfredoStyles.CurrencyPreview>
              </AlfredoStyles.FormGrid>
            </AlfredoStyles.FormSection>

            <AlfredoStyles.FormSection>
              <AlfredoStyles.SectionTitle>
                Controle de Estoque
              </AlfredoStyles.SectionTitle>

              <AlfredoStyles.FormGrid>
                <AlfredoStyles.TextInput>
                  <input
                    type="number"
                    step="0.000001"
                    name="organizationInfoValue.estoqueSeguranca"
                    value={valores.estoqueSeguranca}
                    onChange={handleLocalChange}
                    placeholder="0,000000"
                  />
                  <label>
                    Estoque de Segurança
                    {valores.unidadeEstoque
                      ? ` (${valores.unidadeEstoque})`
                      : ""}
                  </label>
                  <span className="tooltip">
                    Saldo mínimo que não deve ser furado
                  </span>
                </AlfredoStyles.TextInput>
              </AlfredoStyles.FormGrid>

              <AlfredoStyles.CheckboxContainer>
                <AlfredoStyles.CheckboxInput>
                  <input
                    type="checkbox"
                    name="organizationInfoValue.perecivel"
                    checked={valores.perecivel}
                    onChange={handleLocalChange}
                  />
                  <span className="checkmark"></span>
                </AlfredoStyles.CheckboxInput>
                <AlfredoStyles.CheckboxLabel>
                  Perecível
                  <span className="tooltip">
                    Produto que estraga e exige giro
                  </span>
                </AlfredoStyles.CheckboxLabel>
              </AlfredoStyles.CheckboxContainer>

              <AlfredoStyles.CheckboxContainer>
                <AlfredoStyles.CheckboxInput>
                  <input
                    type="checkbox"
                    name="organizationInfoValue.validadeControlada"
                    checked={valores.validadeControlada}
                    onChange={handleLocalChange}
                  />
                  <span className="checkmark"></span>
                </AlfredoStyles.CheckboxInput>
                <AlfredoStyles.CheckboxLabel>
                  Validade controlada
                  <span className="tooltip">
                    Cada entrada exige data de validade no recebimento
                  </span>
                </AlfredoStyles.CheckboxLabel>
              </AlfredoStyles.CheckboxContainer>
            </AlfredoStyles.FormSection>

            <AlfredoStyles.FormSection>
              <AlfredoStyles.SectionTitle>
                Calculados pelo Sistema
              </AlfredoStyles.SectionTitle>

              {/* Travessão, e não zero nem estimativa: estes dois saem de
                  entrada, saída e custo médio — dados que o módulo de movimento
                  ainda não produz. Número inventado aqui vira decisão de compra
                  errada lá fora. */}
              <AlfredoStyles.FormGrid>
                <AlfredoStyles.LabelInputContainer>
                  <AlfredoStyles.Label>
                    Custo unitário corrente
                    <AlfredoStyles.InfoBadge>calculado</AlfredoStyles.InfoBadge>
                  </AlfredoStyles.Label>
                  <AlfredoStyles.Input value="—" readOnly disabled />
                  <AlfredoStyles.CurrencyPreview>
                    Depende do módulo de movimento, que ainda não existe.
                  </AlfredoStyles.CurrencyPreview>
                </AlfredoStyles.LabelInputContainer>

                <AlfredoStyles.LabelInputContainer>
                  <AlfredoStyles.Label>
                    Classe ABC
                    <AlfredoStyles.InfoBadge>calculado</AlfredoStyles.InfoBadge>
                  </AlfredoStyles.Label>
                  <AlfredoStyles.Input value="—" readOnly disabled />
                  <AlfredoStyles.CurrencyPreview>
                    Depende do módulo de movimento, que ainda não existe.
                  </AlfredoStyles.CurrencyPreview>
                </AlfredoStyles.LabelInputContainer>
              </AlfredoStyles.FormGrid>
            </AlfredoStyles.FormSection>

            <AlfredoStyles.FormSection>
              <AlfredoStyles.SectionTitle>
                Informações Comerciais
              </AlfredoStyles.SectionTitle>

              <AlfredoStyles.FormGrid>
                <AlfredoStyles.TextInput>
                  <input
                    type="number"
                    step="0.01"
                    name="organizationInfoValue.skuSuggestedPrice"
                    value={valores.skuSuggestedPrice}
                    onChange={handleLocalChange}
                    placeholder="0,00"
                  />
                  <label>Preço Sugerido (R$)</label>
                  <span className="tooltip">Preço de venda sugerido</span>
                </AlfredoStyles.TextInput>

                <AlfredoStyles.TextInput>
                  <input
                    type="number"
                    step="0.01"
                    name="organizationInfoValue.skuCost"
                    value={valores.skuCost}
                    onChange={handleLocalChange}
                    placeholder="0,00"
                  />
                  <label>Custo (R$)</label>
                  <span className="tooltip">Custo de aquisição/produção</span>
                </AlfredoStyles.TextInput>
              </AlfredoStyles.FormGrid>
            </AlfredoStyles.FormSection>

            <AlfredoStyles.FormSection>
              <AlfredoStyles.SectionTitle>
                Informações Adicionais
              </AlfredoStyles.SectionTitle>

              <AlfredoStyles.FormGrid>
                <AlfredoStyles.TextInput>
                  <input
                    type="text"
                    name="organizationInfoValue.skuBarcode"
                    value={valores.skuBarcode}
                    onChange={handleLocalChange}
                  />
                  <label>Código de Barras</label>
                  <span className="tooltip">Código EAN/UPC para leitura</span>
                </AlfredoStyles.TextInput>

                <AlfredoStyles.TextInput>
                  <input
                    type="number"
                    step="0.001"
                    name="organizationInfoValue.skuWeight"
                    value={valores.skuWeight}
                    onChange={handleLocalChange}
                  />
                  <label>Peso (kg)</label>
                  <span className="tooltip">Peso unitário do produto</span>
                </AlfredoStyles.TextInput>
              </AlfredoStyles.FormGrid>

              <AlfredoStyles.TextInput>
                <input
                  type="text"
                  name="organizationInfoValue.skuDimensions"
                  value={valores.skuDimensions}
                  onChange={handleLocalChange}
                  placeholder="L x A x C (cm)"
                />
                <label>Dimensões</label>
                <span className="tooltip">
                  Dimensões do produto em centímetros
                </span>
              </AlfredoStyles.TextInput>
            </AlfredoStyles.FormSection>

            <AlfredoStyles.FormSection>
              <AlfredoStyles.CheckboxContainer>
                <AlfredoStyles.CheckboxInput>
                  <input
                    type="checkbox"
                    name="organizationInfoValue.skuStatus"
                    checked={valores.skuStatus}
                    onChange={handleLocalChange}
                  />
                  <span className="checkmark"></span>
                </AlfredoStyles.CheckboxInput>
                <AlfredoStyles.CheckboxLabel>
                  SKU Ativo
                  <span className="tooltip">
                    Desmarque para inativar o produto
                  </span>
                </AlfredoStyles.CheckboxLabel>
              </AlfredoStyles.CheckboxContainer>
            </AlfredoStyles.FormSection>

            {aviso && (
              <AlfredoStyles.AlertMessage $tom={aviso.tom}>
                {aviso.texto}
              </AlfredoStyles.AlertMessage>
            )}
          </AlfredoStyles.FormContainer>
        </AlfredoStyles.ModalBody>

        <AlfredoStyles.ModalFooter>
          <AlfredoStyles.ButtonGroup>
            <AlfredoStyles.SecondaryButton onClick={handleCloseModal}>
              <AlfredoStyles.MaterialIcons>close</AlfredoStyles.MaterialIcons>
              Cancelar
            </AlfredoStyles.SecondaryButton>
            <AlfredoStyles.PrimaryButton
              onClick={handleSubmit}
              disabled={salvando}
            >
              <AlfredoStyles.MaterialIcons>save</AlfredoStyles.MaterialIcons>
              {salvando ? "Salvando..." : "Criar SKU"}
            </AlfredoStyles.PrimaryButton>
          </AlfredoStyles.ButtonGroup>
        </AlfredoStyles.ModalFooter>
      </AlfredoStyles.ModalContent>
    </AlfredoStyles.ModalOverlay>,
    document.body
  );
};

export default CreateSKUModal;
